import { AnnotationResult, ProgressCallback, TranslateService } from './types';
import { translateText } from './translator';
import { tokenizeJapanese } from './tokenizer';
import { getWordInfo } from './word';
import { getRomaji, hasKanji, katakanaToHiragana } from './converter';
import { debug, info } from './logger';
import { withErrorHandling } from './error';

// 当前请求ID - 用于取消过时的请求
export let currentRequestId = 0;

/**
 * 清除请求队列
 */
export const clearRequestQueue = () => {
  currentRequestId++;
  debug(`新请求ID: ${currentRequestId}`);
};

/**
 * 处理文本，生成字符映射
 * @param text 要处理的文本
 * @param callback 回调函数
 */
export const processWord = (
  text: string,
  callback: (result: any[]) => void
): number => {
  if (!text || text.trim() === '') {
    callback([]);
    return currentRequestId;
  }

  const requestId = ++currentRequestId;
  debug(`处理文本 "${text}" (请求ID ${requestId})`);

  // 使用异步分词
  tokenizeJapanese(text)
    .then(segments => {
      debug(`分词结果: ${segments.length} 个词段`);

      // 对每个分词结果进行处理
      const segmentPromises = segments.map(segment =>
        processSegment(segment)
      );

      return Promise.all(segmentPromises);
    })
    .then(segmentMappings => {
      // 整合所有分词结果为字符映射列表
      const result = flattenSegmentMappings(segmentMappings, text);

      // 如果请求ID仍然有效，执行回调
      if (requestId === currentRequestId) {
        debug(`完成处理文本 "${text}" (请求ID ${requestId})`);
        callback(result);
      } else {
        debug(`跳过过时的请求 "${text}" (请求ID ${requestId}，当前ID ${currentRequestId})`);
      }
    })
    .catch(error => {
      debug(`处理文本失败 "${text}"`, error);

      // 回退处理
      if (requestId === currentRequestId) {
        const fallbackResult = text.split('').map(char => ({
          character: char,
          romaji: hasKanji(char) ? '?' : getRomaji(char),
          isKanji: hasKanji(char)
        }));
        callback(fallbackResult);
      }
    });

  return requestId;
};

/**
 * 处理单个分词段
 */
const processSegment = async (segment: string) => {
  try {
    // 获取词性信息
    const wordInfo = await getWordInfo(segment);

    // 确保读音是平假名
    const reading = wordInfo.kanjis[0]?.reading ?
      katakanaToHiragana(wordInfo.kanjis[0].reading) : segment;

    // 构建基本信息
    return {
      segment,
      reading: reading,
      romaji: wordInfo.romaji || await getRomaji(reading),
      characters: segment.split('').map(char => ({
        character: char,
        romaji: hasKanji(char) ? '?' : getRomaji(char),
        isKanji: hasKanji(char)
      }))
    };
  } catch (error) {
    debug(`处理分词失败: "${segment}"`, error);
    return {
      segment,
      reading: segment,
      romaji: await getRomaji(segment),
      characters: segment.split('').map(char => ({
        character: char,
        romaji: hasKanji(char) ? '?' : getRomaji(char),
        isKanji: hasKanji(char)
      }))
    };
  }
};

/**
 * 将分词段映射扁平化为字符映射列表
 */
const flattenSegmentMappings = (segmentMappings: any[], originalText: string): any[] => {
  debug(`合并 ${segmentMappings.length} 个分词结果`);

  // 如果没有分词结果，直接返回字符级处理
  if (!segmentMappings || segmentMappings.length === 0) {
    return originalText.split('').map(char => ({
      character: char,
      romaji: hasKanji(char) ? '?' : getRomaji(char),
      isKanji: hasKanji(char)
    }));
  }

  // 重建分词结果，确保每个字符都有对应的映射
  const result: any[] = [];
  let currentIndex = 0;

  for (const mapping of segmentMappings) {
    const segment = mapping.segment;

    if (!segment) {
      continue;
    }

    // 处理每个字符
    for (let i = 0; i < segment.length; i++) {
      const char = segment[i];

      // 确保当前索引不超出原始文本长度
      if (currentIndex < originalText.length) {
        // 如果原始文本中的字符与分词结果不匹配，可能是分词错误
        if (originalText[currentIndex] !== char) {
          debug(`字符不匹配: 原始文本 "${originalText[currentIndex]}" 与分词结果 "${char}"`);

          // 添加原始文本中的字符
          result.push({
            character: originalText[currentIndex],
            romaji: hasKanji(originalText[currentIndex]) ? '?' : getRomaji(originalText[currentIndex]),
            isKanji: hasKanji(originalText[currentIndex])
          });
        } else {
          // 字符匹配，使用分词结果中的映射
          const charMapping = mapping.characters[i];
          if (charMapping) {
            result.push(charMapping);
          } else {
            // 如果分词结果中没有对应的映射，创建一个新的
            result.push({
              character: char,
              romaji: hasKanji(char) ? '?' : getRomaji(char),
              isKanji: hasKanji(char)
            });
          }
        }

        currentIndex++;
      }
    }
  }

  // 处理剩余的字符
  while (currentIndex < originalText.length) {
    const char = originalText[currentIndex];
    result.push({
      character: char,
      romaji: hasKanji(char) ? '?' : getRomaji(char),
      isKanji: hasKanji(char)
    });
    currentIndex++;
  }

  // 确保结果与原始文本长度一致
  if (result.length !== originalText.length) {
    debug(`字符映射长度 (${result.length}) 与原文本长度 (${originalText.length}) 不一致，进行调整`);

    // 如果结果长度大于原始文本长度，截断多余部分
    if (result.length > originalText.length) {
      result.splice(originalText.length);
    }

    // 如果结果长度小于原始文本长度，添加缺失部分
    while (result.length < originalText.length) {
      const index = result.length;
      const char = originalText[index];
      result.push({
        character: char,
        romaji: hasKanji(char) ? '?' : getRomaji(char),
        isKanji: hasKanji(char)
      });
    }
  }

  return result;
};

/**
 * 一站式从中文生成日语注释
 * 包括翻译、分词、假名和罗马音标注
 */
export const annotateJapaneseFromChinese = withErrorHandling(
  async (
    chineseText: string,
    service: TranslateService = TranslateService.Google,
    onProgress?: ProgressCallback
  ): Promise<AnnotationResult> => {
    if (!chineseText || chineseText.trim() === '') {
      return {
        japaneseText: '',
        annotation: {
          segments: [],
          readings: [],
          romaji: []
        },
        formattedAnnotation: ''
      };
    }

    // 1. 翻译中文到日语
    onProgress?.(10, '正在翻译中文...');
    const japaneseText = await translateText(
      chineseText,
      'zh-CN',
      'ja',
      service
    );

    // 2. 对日语文本进行分词
    onProgress?.(30, '正在进行分词分析...');
    const segments = await tokenizeJapanese(japaneseText, onProgress);

    // 3. 获取每个分词的读音和罗马音
    const annotation = {
      segments: segments,
      readings: [] as string[],
      romaji: [] as string[]
    };

    // 4. 并行获取每个分词的读音和罗马音
    onProgress?.(50, '正在获取读音和罗马音...');
    const wordInfoPromises = segments.map(segment => getWordInfo(segment));
    const wordInfoResults = await Promise.all(wordInfoPromises);

    // 5. 处理结果
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const wordInfo = wordInfoResults[i];

      // 获取读音
      let reading = '';

      // 如果是汉字词，需要特殊处理
      if (wordInfo.isKanjiWord && wordInfo.kanjis && wordInfo.kanjis.length > 0) {
        // 对于每个汉字，获取其读音
        const readings = wordInfo.kanjis.map(k => k.reading || k.kanji);
        reading = readings.join('');
      } else if (wordInfo.kanjis && wordInfo.kanjis.length > 0) {
        // 非汉字词，使用第一个读音
        reading = wordInfo.kanjis[0].reading || segment;
      } else {
        // 没有读音信息，使用原始分词
        reading = segment;
      }

      annotation.readings.push(reading);

      // 获取罗马音
      let romaji = '';
      if (wordInfo.romaji) {
        romaji = wordInfo.romaji;
      } else {
        romaji = await getRomaji(reading);
      }
      annotation.romaji.push(romaji);
    }

    // 6. 生成格式化的标注文本
    onProgress?.(80, '正在生成标注文本...');
    let formattedAnnotation = '';
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const reading = annotation.readings[i];

      // 如果有假名且与原文不同，添加假名标注
      if (hasKanji(segment) && reading && reading !== segment) {
        formattedAnnotation += `${segment}(${reading}) `;
      } else {
        formattedAnnotation += `${segment} `;
      }
    }

    onProgress?.(100, '完成');
    return {
      japaneseText,
      annotation,
      formattedAnnotation: formattedAnnotation.trim()
    };
  },
  // 默认值
  {
    japaneseText: '',
    annotation: {
      segments: [],
      readings: [],
      romaji: []
    },
    formattedAnnotation: ''
  },
  '日语注释生成'
);